import {Button, createStyles, Group, Stack, Title} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import EntryCalendar from 'calendar/EntryCalendar';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import EntityStore from 'stores/EntityStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ActivitySection from './homepage/ActivitySection';
import {MAX_CONTENT_WIDTH} from './homepage/HomepageConstants';
import HomepagePreviewBanner from './homepage/HomepagePreviewBanner';
import {shouldShowWorklistsSection} from './homepage/HomepageVisibility';
import MyWorkSection from './homepage/MyWorkSection';
import RecentsSection from './homepage/RecentsSection';
import WorklistsSection from './homepage/WorklistsSection';

const COLUMN_SPACING = '26px';

const useCardsStyles = createStyles((theme, {width = 1000}: {width?: number}) => ({
  columnContainer: {
    maxWidth: MAX_CONTENT_WIDTH,
    margin: '0 auto',
  },
  columnContainerWrapper: {
    flexGrow: 1,
    padding: `${theme.spacing.lg} ${COLUMN_SPACING}`,
    backgroundColor: theme.colors.gray[0],
  },
  column: {
    width: width < 1000 ? '100%' : `calc(50% - ${COLUMN_SPACING} / 2)`,
  },
}));

const useHomepageStyles = createStyles((theme) => ({
  closeIcon: {
    cursor: 'pointer',
    alignSelf: 'flex-start',
    '&&': {
      height: 18,
      width: 18,
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  negativeXsMarginLeft: {
    marginLeft: `-${theme.spacing.xs}`,
  },
  xsMarginRight: {
    marginRight: theme.spacing.xs,
  },
}));

const HomepageCards = (): JSX.Element => {
  const {ref, width} = useResizeObserver();
  const {classes} = useCardsStyles({width});

  return (
    <div className={classes.columnContainerWrapper} ref={ref}>
      <HomepagePreviewBanner />
      <Group
        position="center"
        spacing={COLUMN_SPACING}
        className={classes.columnContainer}
        align="flex-start"
      >
        <Stack className={classes.column}>
          <RecentsSection />
          {shouldShowWorklistsSection() && <WorklistsSection />}
        </Stack>
        <Stack className={classes.column}>
          <MyWorkSection />
          <ActivitySection />
        </Stack>
      </Group>
    </div>
  );
};

/** Preference-gated home sidepanel with recents, worklists, my work, and activity sections. */
export default function Homepage(): JSX.Element {
  const [showCalendar, setShowCalendar] = useState<boolean>(false);

  const user = useStoreSelector(
    EntityStore,
    useCallback((store) => store.currentUser(), [])
  );

  useOnMount(() => {
    SidepanelActions.expand();
  });

  const {classes, cx} = useHomepageStyles();

  return (
    <div className={cx(classes.container, 'sidepanel')}>
      <Stack m="24px" spacing="0">
        <Group position="apart" noWrap={true} align="flex-start">
          <Group spacing="xl">
            <Title order={3}>Welcome back{user != null ? `, ${user.name}` : ''}</Title>
            <Button
              size="sm"
              // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design, yet
              variant="light"
              className={classes.negativeXsMarginLeft}
              onClick={() => {
                setShowCalendar(!showCalendar);
              }}
            >
              {showCalendar ? (
                'Back to home'
              ) : (
                <>
                  <BenchlingIcon className={classes.xsMarginRight} icon="calendar" /> My Calendar
                </>
              )}
            </Button>
          </Group>
          <BenchlingIcon
            icon="close"
            altText="Close"
            className={classes.closeIcon}
            {...getActionButtonRoleProps({
              onPress: () => {
                SidepanelActions.close();
              },
            })}
          />
        </Group>
      </Stack>
      {/* Inexplicably, EntryCalendar has only left and top margin */}
      {showCalendar ? (
        <div className="u-spacing-right">
          <EntryCalendar />
        </div>
      ) : (
        <HomepageCards />
      )}
    </div>
  );
}
